import React, { useState, useEffect, useCallback } from 'react'
import { useLocation } from '@reach/router'
import ArchiveIcon from '../assets/ArchiveIcon'
import DocumentIcon from '../assets/DocumentIcon'
import MessageIcon from '../assets/MessageIcon'
import TruckIcon from '../assets/TruckIcon'
import { useFormContext } from '../context/form.context'
import { useLangContext } from '../context/lang.context'
import useGadSource from '../utils/useGadSource'
import {
  alpha,
  colors,
  s,
  sInput,
  sTextArea,
  sInputError,
} from '../style'
import Headline from './Headline'
import Grid from './Grid'
import { validateEmail } from '../utils'
import FormAgreements, { agreementsContent } from './FormAgreements'
import FormFeedback from './FormFeedback'
import Button from './Button'
import AdministrativeFormInfo from './AdministrativeFormInfo'

import axios from 'axios'
import Select from 'react-select'
import GooglePlacesAutocomplete from 'react-google-places-autocomplete'
import 'react-google-places-autocomplete/dist/index.min.css'

const FormDynamic = ({ popup, notypes, override }) => {
  const location = useLocation()
  const { lang } = useLangContext()
  const gadSource = useGadSource()

  const {
    formType: formTypeGlobal,
    setFormOpen,
    setFormType: setFormTypeGlobal,
  } = useFormContext()
  const [formType, setFormType] = useState(override || formTypeGlobal)
  const currentForm = types.find((form) => form.type === formType)

  const [formAgreements, setFormAgreements] = useState([])

  const initialCargo = {
    width: '',
    length: '',
    height: '',
    weight: '',
    quantity: '',
  }

  const initialFormData = {
    placeFrom: '',
    placeTo: '',
    dateStart: '', // dateFrom.toISOString().slice(0, 10),
    dateEnd: '', // dateTo.toISOString().slice(0, 10),
    tenderDateStart: '', // tenderDateStart.toISOString().slice(0, 10),
    tenderDateEnd: '', // tenderDateEnd.toISOString().slice(0, 10)
    message: '',
    cargos: [],
    email: '',
    phone: '',
    firstName: '',
    lastName: '',
    company: '',
    nip: '',
    placeFromId: '',
    placeToId: '',
    carrierServiceType: [],
    carrierTruckType: [],
    carrierCarType: [],
    carrierTrailerType: [],
    carrierDrivingSystem: [],
    carrierLocation: '',
    agreements: [],
  }

  const createInitialAgreements = (agreements) => {
    return agreements.map(agreement => ({
      subAgreements: agreement.subAgreements.map(sub => {
        if (sub.options) {
          return {
            values: Array(sub.options.length).fill(false)
          }
        }
        return false
      })
    }))
  }

  const [formData, setFormData] = useState(initialFormData)
  const [mailState, setMailState] = useState('unfilled')
  const [success, setSuccess] = useState(null)
  const [openBackdrop, setOpenBackdrop] = useState(false)
  const [wasSent, setWasSent] = useState(false)
  const [isCiagnikSelected, setIsCiagnikSelected] = useState(false)
  const formDataEmail = formData.email

  // Initialize formAgreements on component mount
  useEffect(() => {
    const initialAgreements = createInitialAgreements(agreementsContent.agreements)
    setFormAgreements(initialAgreements)
  }, [])

  // **New UseEffect to Reset Agreements When formType Changes**
  useEffect(() => {
    const initialAgreements = createInitialAgreements(agreementsContent.agreements)
    setFormAgreements(initialAgreements)
  }, [formType])

  const getContentWithDynamicRequired = () => {
    const updatedFields = {
      ...content.fields,
      carrierTruckType: {
        ...content.fields.carrierTruckType,
        isRequired: isCiagnikSelected ? ['CARRIER'] : [],
      },
    }

    return {
      ...content,
      fields: updatedFields,
    }
  }

  const dynamicContent = getContentWithDynamicRequired()

  const isRequiredFilled = useCallback(() => {
    const fields = getContentWithDynamicRequired().fields

    return Object.entries(fields)
      .map(([key, value]) => {
        if (value.isRequired?.includes(formType)) {
          const fieldData = formData[key]
          if (typeof fieldData === 'string') {
            return fieldData.trim().length > 0
          } else if (Array.isArray(fieldData)) {
            return fieldData.length > 0
          }
        }
        return true
      })
      .reduce((acc, curr) => acc && curr, true)
  }, [formData, formType, isCiagnikSelected])

  const areRequiredAgreementsChecked = useCallback(() => {
    if (!formAgreements || formAgreements.length === 0) return false;
    
    // Check first agreement group (which is required as per the code)
    const firstAgreement = formAgreements[0];
    if (!firstAgreement || !firstAgreement.subAgreements) return false;

    // Check if required sub-agreement is checked (index 1 is the required one based on FormAgreements.js)
    const requiredSubAgreement = firstAgreement.subAgreements[1];
    return requiredSubAgreement === true;
  }, [formAgreements]);

  // Extended validate
  const validate = useCallback(() => {
    const missingFields = []

    if (!isRequiredFilled()) missingFields.push('required fields not filled')
    if (!validateEmail(formDataEmail)) missingFields.push('invalid email')
    if (!areRequiredAgreementsChecked()) missingFields.push('agreements not checked')

    if (missingFields.length > 0) {
      // console.log('Form validation failed:', missingFields);
      return false
    }

    return true
  }, [isRequiredFilled, formDataEmail, areRequiredAgreementsChecked])

  const onValueChange = (event) => {
    // Handle standard HTML inputs
    if (event.target) {
      setFormData({
        ...formData,
        [event.target.name]: event.target.value,
      })
    }
  }

  const handleSelectChange = (selectedOptions, name) => {
    const selectedData = selectedOptions.map((option) => ({
      value: option.value,
      label: option.label[lang],
    }))

    if (name === 'carrierCarType') {
      const hasCiagnik = selectedOptions.some(
        (option) => option.value === 'ciagnik'
      )

      // Update `isCiagnikSelected` and clear `carrierTruckType` if "ciagnik" is deselected
      setIsCiagnikSelected(hasCiagnik)

      if (!hasCiagnik) {
        setFormData((prevData) => ({
          ...prevData,
          carrierTruckType: [], // Clear carrierTruckType data if "ciagnik" is deselected
        }))
      }
    }

    setFormData((prevData) => ({
      ...prevData,
      [name]: selectedData,
    }))
  }

  // let isMessage = formType === 'MESSAGE'
  const isQuote = formType === 'QUOTE'
  const isCollab = formType === 'COLLAB'
  const isCarrier = formType === 'CARRIER'

  const sendMail = async () => {
    setWasSent(true)

    if (validate()) {
      setSuccess(null)
      setOpenBackdrop(true)
      setMailState('loading')

      // Przewoźnik
      if (isCarrier) {
        if (
          formData.email.includes('@omida.pl') ||
          formData.email.includes('@seaandair.pl')
        ) {
        } else {
          axios({
            method: 'POST',
            url:
              process.env.NODE_ENV === 'development'
                ? 'http://localhost:4000/hubspot-omida-fleet/init'
                : 'https://cleverserver.appclever.pl/hubspot-omida-fleet/init',
            data: {
              website: location.href,
              ...formData,
              lang,
            },
            headers: {
              'Content-Type': 'application/json',
              'Access-Control-Allow-Origin': '*',
            },
          })
            .then((res) => {
              if (res.status === 200) {
                setOpenBackdrop(true)
                setFormData(initialFormData)
                setFormAgreements(createInitialAgreements(agreementsContent.agreements));
                setSuccess(true)
                setWasSent(false)
              }
            })
            .catch((error) => {
              console.log(error)
              setOpenBackdrop(true)
              setSuccess(false)
            })
        }
      }

      // Przetargi
      if (isCollab) {
        if (
          formData.email.includes('@omida.pl') ||
          formData.email.includes('@seaandair.pl')
        ) {
        } else {
          axios({
            method: 'POST',
            url:
              process.env.NODE_ENV === 'development'
                ? 'http://localhost:4000/hubspot-omida-tender/init'
                : 'https://cleverserver.appclever.pl/hubspot-omida-tender/init',
            data: {
              website: location.href,
              ...formData,
              lang,
            },
            headers: {
              'Content-Type': 'application/json',
              'Access-Control-Allow-Origin': '*',
            },
          })
            .then((res) => {
              if (res.status === 200) {
                setOpenBackdrop(true)
                setFormData(initialFormData)
                setFormAgreements(createInitialAgreements(agreementsContent.agreements));
                setSuccess(true)
                setWasSent(false)
              }
            })
            .catch((error) => {
              console.log(error)
              setOpenBackdrop(true)
              setSuccess(false)
            })
        }
      }

      if (!isCollab && !isCarrier) {
        if (
          formData.email.includes('@omida.pl') ||
          formData.email.includes('@seaandair.pl')
        ) {
        } else {
          axios({
            method: 'POST',
            url:
              process.env.NODE_ENV === 'development'
                ? 'http://localhost:4000/hubspot-omida-vls/init'
                : 'https://cleverserver.appclever.pl/hubspot-omida-vls/init',
            data: {
              website: location.href,
              source: gadSource ? 'GoogleAds' : 'WWW',
              lang,
              ...formData,
            },
            headers: {
              'Content-Type': 'application/json',
              'Access-Control-Allow-Origin': '*',
            },
          })
            .then((res) => {
              if (res.status === 200) {
                setOpenBackdrop(true)
                setFormData(initialFormData)
                setFormAgreements(createInitialAgreements(agreementsContent.agreements));
                setSuccess(true)
                setWasSent(false)
                sessionStorage.removeItem("gadSource");
              }
            })
            .catch((error) => {
              console.log(error)
              setOpenBackdrop(true)
              setSuccess(false)
            })
        }
      }

      // Newsletter
      axios({
        method: 'POST',

        url:
          process.env.NODE_ENV === 'development'
            ? 'http://localhost:4000/omida/newsletter/init'
            : 'https://cleverserver.appclever.pl/omida/newsletter/init',
        data: {
          ...formData,
          campaignToken: formType === 'CARRIER' ? 'ZcQUm' : 'jAAKH',
          formSent: true,
          lang,
        },
        headers: {
          'Content-Type': 'application/json',
          'Access-Control-Allow-Origin': '*',
        },
      })
      .then((res) => {
        if (res.status === 200) {
          console.log(res)
          setOpenBackdrop(true)
          setFormData(initialFormData)
          setFormAgreements(createInitialAgreements(agreementsContent.agreements));
          setSuccess(true)
          setWasSent(false)
        }
      })
      .catch((error) => {
        console.log(error)
        setOpenBackdrop(true)
        setSuccess(false)
      })
    }
  }

  const onEscape = (event) => {
    if (event.keyCode === 27) {
      setFormOpen(false)
    }
  }

  // Trigger validation check when `isCiagnikSelected` changes
  useEffect(() => {
    validate()
  }, [isCiagnikSelected, validate])

  useEffect(() => {
    if (validate()) setMailState('filled')
    else setMailState('unfilled')
  }, [validate, setMailState])

  useEffect(() => {
    setWasSent(false)
    if (!override) setFormType(formTypeGlobal)
  }, [formTypeGlobal, override])

  /* eslint-disable */
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.addEventListener('keydown', onEscape, false)
    }
  }, [])

  useEffect(() => {
    if (override) setFormType(override)
  }, [override])
  /* eslint-enable */

  const agreementFilled = areRequiredAgreementsChecked()

  return (
    <>
      <div css={sContainer}>
        {!notypes && (
          <div css={sTypes}>
            {types.map(({ type, name, icon }, id) => (
              <div
                key={id}
                css={[sType, type === formType && sTypeActive]}
                onClick={() => setFormTypeGlobal(type)}
                onKeyPress={() => setFormTypeGlobal(type)}
                tabIndex={0}
                role='button'>
                <span css={sIcon}>{icon()}</span>
                <span css={{ [s.sm_down]: { display: 'none' } }}>
                  {name[lang]}
                </span>
              </div>
            ))}
          </div>
        )}
        <Headline
          medium
          gradient
          maxWidth
          title={currentForm.title[lang]}
          desc={currentForm.desc[lang]}
        />
        <Grid container ccss={sFormContainer}>
          {isQuote && (
            <Grid
              item
              xs={12}
              md={6}
              ccss={[
                {
                  '.google-places-autocomplete, .G-input-wrapper': {
                    margin: '0 !important',
                  },
                },
                sPadding(true),
              ]}>
              <GooglePlacesAutocomplete
                required
                onSelect={(response) => {
                  // console.log(response)
                  setFormData({
                    ...formData,
                    placeFrom: response.description,
                    placeFromId: response.place_id,
                  })
                }}
                loader={<div css={{ display: 'none' }} />}
                renderInput={(props) => {
                  const isRequired =
                    content.fields.placeFrom.isRequired.includes(formType)
                  const newProps = {
                    ...props,
                    placeholder: `${content.fields.placeFrom.label[lang]} ${
                      isRequired ? ' *' : ''
                    }`,
                  }

                  return (
                    <input
                      name='placeFrom'
                      type='text'
                      label={content.fields.placeFrom.label[lang]}
                      required={isRequired}
                      onChange={onValueChange}
                      onChangeCapture={(event) => {
                        setFormData({
                          ...formData,
                          placeFrom: event.target.value,
                        })
                      }}
                      value={formData['placeFrom']}
                      css={[
                        sInput,
                        wasSent &&
                          isRequired &&
                          !formData.placeFrom &&
                          sInputError,
                      ]}
                      {...newProps}
                    />
                  )
                }}
                renderSuggestions={(
                  active,
                  suggestions,
                  onSelectSuggestion
                ) => (
                  <div css={sSuggestions}>
                    {suggestions.map((suggestion, id) => (
                      <div
                        key={id}
                        css={sSugestion}
                        onClick={(event) => {
                          onSelectSuggestion(suggestion, event)
                        }}
                        onKeyPress={(event) => {
                          onSelectSuggestion(suggestion, event)
                        }}
                        tabIndex={0}
                        role='button'>
                        {suggestion.description}
                      </div>
                    ))}
                  </div>
                )}
                apiKey={process.env.GATSBY_GOOGLE_API}
              />
            </Grid>
          )}
          {isQuote && (
            <Grid
              item
              xs={12}
              md={6}
              ccss={[
                {
                  '.google-places-autocomplete, .G-input-wrapper': {
                    margin: '0 !important',
                  },
                },
                sPadding(false),
              ]}>
              <GooglePlacesAutocomplete
                required
                onSelect={(response) => {
                  // console.log(response)
                  setFormData({
                    ...formData,
                    placeTo: response.description,
                    placeToId: response.place_id,
                  })
                }}
                loader={<div css={{ display: 'none' }} />}
                renderInput={(props) => {
                  const isRequired =
                    content.fields.placeTo.isRequired.includes(formType)
                  const newProps = {
                    ...props,
                    placeholder: `${content.fields.placeTo.label[lang]} ${
                      isRequired ? ' *' : ''
                    }`,
                  }

                  return (
                    <input
                      name='placeTo'
                      type='text'
                      label={content.fields.placeTo.label[lang]}
                      placeholder={content.fields.placeTo.label[lang]}
                      required={isRequired}
                      onChange={onValueChange}
                      onChangeCapture={(event) => {
                        setFormData({
                          ...formData,
                          placeTo: event.target.value,
                        })
                      }}
                      value={formData['placeTo']}
                      css={[
                        sInput,
                        wasSent &&
                          isRequired &&
                          !formData.placeTo &&
                          sInputError,
                      ]}
                      {...newProps}
                    />
                  )
                }}
                renderSuggestions={(
                  active,
                  suggestions,
                  onSelectSuggestion
                ) => (
                  <div css={sSuggestions}>
                    {suggestions.map((suggestion, id) => (
                      <div
                        key={id}
                        css={sSugestion}
                        onClick={(event) => {
                          onSelectSuggestion(suggestion, event)
                        }}
                        onKeyPress={(event) => {
                          onSelectSuggestion(suggestion, event)
                        }}
                        tabIndex={0}
                        role='button'>
                        {suggestion.description}
                      </div>
                    ))}
                  </div>
                )}
                apiKey={process.env.GATSBY_GOOGLE_API}
              />
            </Grid>
          )}
          {isQuote && (
            <Grid
              item
              xs={12}
              md={6}
              ccss={[sPadding(true), sInputHiderParent]}>
              <input
                name='dateStart'
                type={content.fields.dateStart.type}
                label={content.fields.dateStart.label[lang]}
                placeholder={content.fields.dateStart.label[lang]}
                required={content.fields.dateStart.isRequired.includes(
                  formType
                )}
                onChange={onValueChange}
                value={formData['dateStart']}
                css={[
                  sInput,
                  { minHeight: 50 },
                  !formData['dateStart'] && {
                    input: {
                      color: `${colors.greyDark.concat(alpha[0])} !important`,
                    },
                  },
                ]}
              />
              <div
                className='input-hider'
                css={[sInputHider, formData.dateStart && { opacity: 0 }]}>
                {content.fields.dateStart.label[lang]}
              </div>
            </Grid>
          )}
          {isQuote && (
            <Grid
              item
              xs={12}
              md={6}
              ccss={[sPadding(false), sInputHiderParent]}>
              <input
                name='dateEnd'
                type={content.fields.dateEnd.type}
                label={content.fields.dateEnd.label[lang]}
                placeholder={content.fields.dateEnd.label[lang]}
                required={content.fields.dateEnd.isRequired.includes(formType)}
                onChange={onValueChange}
                value={formData['dateEnd']}
                css={[
                  sInput,
                  { minHeight: 50 },
                  !formData['dateEnd'] && {
                    input: {
                      color: `${colors.greyDark.concat(alpha[0])} !important`,
                    },
                  },
                ]}
              />
              <div
                className='input-hider'
                css={[
                  sInputHider,
                  { [s.md]: { marginLeft: 12 } },
                  formData.dateEnd && { opacity: 0 },
                ]}>
                {content.fields.dateEnd.label[lang]}
              </div>
            </Grid>
          )}

          {isCollab &&
            (() => {
              const isRequiredDateStart = content.fields.tenderDateStart.isRequired.includes(formType)
              const isRequiredDateEnd = content.fields.tenderDateEnd.isRequired.includes(formType)
              // wasSent && isRequired && !formData.company && sInputError,
              return (
                <>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    css={[sPadding(true), sInputHiderParent]}>
                    <input
                      name='tenderDateStart'
                      type='text'
                      label={content.fields.tenderDateStart.label[lang]}
                      placeholder={content.fields.tenderDateStart.label[lang]}
                      required={isRequiredDateStart}
                      onChange={onValueChange}
                      onFocus={(e) => e.target.type = 'date'}
                      onBlur={(e) => wasSent && !formData.tenderDateEnd ? e.target.type = 'text' : e.target.type = 'date'}
                      value={formData['tenderDateStart']}
                      css={[
                        sInput,
                        { minHeight: 50 },
                        wasSent &&
                        isRequiredDateEnd &&
                          !formData.tenderDateStart &&
                          sInputError,
                      ]}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    ccss={[sPadding(false), sInputHiderParent]}>
                    <input
                      name='tenderDateEnd'
                      type='text'
                      label={content.fields.tenderDateEnd.label[lang]}
                      placeholder={content.fields.tenderDateEnd.label[lang]}
                      required={isRequiredDateEnd}
                      onChange={onValueChange}
                      onFocus={(e) => e.target.type = 'date'}
                      onBlur={(e) => wasSent && !formData.tenderDateEnd ? e.target.type = 'text' : e.target.type = 'date'}
                      value={formData['tenderDateEnd']}
                      css={[
                        sInput,
                        { minHeight: 50 },
                        wasSent &&
                        isRequiredDateEnd &&
                          !formData.tenderDateEnd &&
                          sInputError,                      
                      ]}
                      />
                  </Grid>
                </>
              )
            })()}

          {isQuote && (
            <Grid
              item
              xs={12}
              ccss={{
                margin: '2rem 0',
                [s.sm_down]: {
                  margin: '1.5rem 0',
                },
              }}>
              <div
                css={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: '1.5rem'
                }}>
                <p css={[sSectionName, sCargoLabel]}>
                  {content.sections.cargos[lang]} (
                  {formData.cargos.reduce(
                    (sum, curr) => parseInt(curr.quantity || 1) + sum,
                    0
                  )}
                  )
                  <br/><span css={{fontSize: '13.5px'}}>Nie obsługujemy przesyłek kurierskich.</span>
                </p>
                <Button
                  noMargin
                  glow
                  extraCss={sButtonExtra}
                  action={() => {
                    setFormData({
                      ...formData,
                      cargos: [...formData.cargos, { ...initialCargo }],
                    })
                  }}>
                  {content.dictonary.cargoAdd[lang]}
                </Button>
              </div>
            </Grid>
          )}
          {isQuote &&
            formData.cargos.map((cargo, id) => (
              <Grid
                item
                xs={12}
                key={id}
              >
                <Grid
                  container
                  ccss={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                  {Object.keys(cargo).map((key, keyId) => (
                    <Grid
                      key={keyId}
                      item
                      xs={12}
                      sm={4}
                      md={4}
                      ccss={[
                        { [s.md]: { paddingRight: '1.5rem' } },
                      ]}>
                      <input
                        min={content.fields[key] === 'quantity' ? '1' : '0'}
                        step='1'
                        key={keyId}
                        name={key}
                        type={content.fields[key].type}
                        label={content.fields[key].label[lang]}
                        value={formData.cargos[id][key].split(' ')[0]}
                        placeholder={content.fields[key].label[lang]}
                        onChange={(e) => {
                          let newCargos = [...formData.cargos]
                          newCargos[id][key] = e.target.value
                          setFormData({
                            ...formData,
                            cargos: [...newCargos],
                          })
                        }}
                        css={sInput}
                      />
                    </Grid>
                  ))}
                  <Grid
                    item
                    xs={12}
                    sm={4}
                    md={4}
                    ccss={{
                      display: 'flex',
                      justifyContent: 'flex-end',

                      marginBottom: '2rem',
                      [s.sm_down]: {
                        marginBottom: '1.5rem',
                      },
                      [s.sm]: {
                        alignItems: 'center',
                      },
                    }}>
                    <Button
                      noMargin
                      extraCss={sButtonExtra}
                      reactive
                      action={() => {
                        setFormData({
                          ...formData,
                          cargos: [
                            ...formData.cargos.filter(
                              (cargo, filteringId) => filteringId !== id
                            ),
                          ],
                        })
                      }}>
                      {content.dictonary.cargoDelete[lang]}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            ))}
          {isQuote && (
            <Grid
              item
              xs={12}
              ccss={{
                margin: '2rem 0',
                [s.sm_down]: {
                  margin: '1.5rem 0',
                },
              }}>
              <p css={sSectionName}>{content.sections.contact[lang]}</p>
            </Grid>
          )}

          {/* carrierServiceType, carrierTruckType, carrierCarType, carrierTrailerType */}

          {isCarrier &&
            (() => {
              const options_carrierTrailerType =
                content.fields.carrierTrailerType.options
              const options_carrierCarType =
                content.fields.carrierCarType.options
              const options_carrierTruckType =
                dynamicContent.fields.carrierTruckType.options
              const options_carrierServiceType =
                content.fields.carrierServiceType.options
              const options_carrierDrivingSystem = content.fields.carrierDrivingSystem.options

              return (
                <>
                  <Grid item xs={12} md={12}>
                    <Select
                      name='carrierTrailerType'
                      isMulti
                      placeholder={
                        content.fields.carrierTrailerType.label[lang]
                      }
                      options={options_carrierTrailerType}
                      getOptionLabel={(option) => option.label[lang]} // Display the 'pl' part of the label
                      getOptionValue={(option) => option.value}
                      onChange={(selectedOptions) =>
                        handleSelectChange(
                          selectedOptions,
                          'carrierTrailerType'
                        )
                      }
                      className='basic-multi-select'
                      classNamePrefix='select'
                      styles={selectStyles(
                        wasSent,
                        formData,
                        'carrierTrailerType'
                      )}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <Select
                      name='carrierCarType'
                      isMulti
                      placeholder={content.fields.carrierCarType.label[lang]}
                      options={options_carrierCarType}
                      getOptionLabel={(option) => option.label[lang]} // Display the 'pl' part of the label
                      getOptionValue={(option) => option.value}
                      onChange={(selectedOptions) =>
                        handleSelectChange(selectedOptions, 'carrierCarType')
                      }
                      className='basic-multi-select'
                      classNamePrefix='select'
                      styles={selectStyles(wasSent, formData, 'carrierCarType')}
                      required
                    />
                  </Grid>
                  {isCiagnikSelected && (
                    <>
                      <Grid item xs={12} md={12}>
                        <Select
                          name='carrierTruckType'
                          isMulti
                          placeholder={
                            dynamicContent.fields.carrierTruckType.label[lang]
                          }
                          options={options_carrierTruckType}
                          getOptionLabel={(option) => option.label[lang]} // Display the 'pl' part of the label
                          getOptionValue={(option) => option.value}
                          onChange={(selectedOptions) =>
                            handleSelectChange(
                              selectedOptions,
                              'carrierTruckType'
                            )
                          }
                          className='basic-multi-select'
                          classNamePrefix='select'
                          styles={selectStyles(
                            wasSent,
                            formData,
                            'carrierTruckType'
                          )}
                          required
                        />
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12} md={12}>
                    <Select
                      name='carrierServiceType'
                      isMulti
                      placeholder={
                        content.fields.carrierServiceType.label[lang]
                      }
                      options={options_carrierServiceType}
                      getOptionLabel={(option) => option.label[lang]} // Display the 'pl' part of the label
                      getOptionValue={(option) => option.value}
                      onChange={(selectedOptions) =>
                        handleSelectChange(
                          selectedOptions,
                          'carrierServiceType'
                        )
                      }
                      className='basic-multi-select'
                      classNamePrefix='select'
                      styles={selectStyles(
                        wasSent,
                        formData,
                        'carrierServiceType'
                      )}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <Select 
                      name='carrierDrivingSystem'
                      isMulti
                      placeholder={content.fields.carrierDrivingSystem.label[lang]}
                      options={options_carrierDrivingSystem}
                      getOptionLabel={(option) => option.label[lang]} // Display the 'pl' part of the label
                      getOptionValue={(option) => option.value}
                      onChange={(selectedOptions) => handleSelectChange(selectedOptions, 'carrierDrivingSystem')}
                      className="basic-multi-select"
                      classNamePrefix="select"
                      styles={selectStyles(wasSent, formData, 'carrierDrivingSystem')} 
                      required
                    />
                  </Grid>
                </>
              )
            })()}

          {isCarrier &&
            (() => {
              return (
                <Grid
                  item
                  xs={12}
                  md={12}
                  ccss={[
                    {
                      '.google-places-autocomplete, .G-input-wrapper': {
                        margin: '0 !important',
                      },
                    },
                  ]}>
                  <GooglePlacesAutocomplete
                    required
                    onSelect={(response) => {
                      setFormData({
                        ...formData,
                        carrierLocation: response.description,
                        carrierLocationId: response.place_id,
                      })
                    }}
                    loader={<div css={{ display: 'none' }} />}
                    renderInput={(props) => {
                      const isRequired =
                        content.fields.carrierLocation.isRequired.includes(
                          formType
                        )
                      const newProps = {
                        ...props,
                        placeholder: `${
                          content.fields.carrierLocation.label[lang]
                        } ${isRequired ? ' *' : ''}`,
                      }

                      return (
                        <input
                          name='carrierLocation'
                          type='text'
                          label={content.fields.carrierLocation.label[lang]}
                          placeholder={
                            content.fields.carrierLocation.label[lang]
                          }
                          required={isRequired}
                          onChange={onValueChange}
                          onChangeCapture={(event) => {
                            setFormData({
                              ...formData,
                              carrierLocation: event.target.value,
                            })
                          }}
                          value={formData['carrierLocation']}
                          css={[
                            sInput,
                            wasSent &&
                              isRequired &&
                              !formData.carrierLocation &&
                              sInputError,
                          ]}
                          {...newProps}
                        />
                      )
                    }}
                    renderSuggestions={(
                      active,
                      suggestions,
                      onSelectSuggestion
                    ) => (
                      <div css={sSuggestions}>
                        {suggestions.map((suggestion, id) => (
                          <div
                            key={id}
                            css={sSugestion}
                            onClick={(event) => {
                              onSelectSuggestion(suggestion, event)
                            }}
                            onKeyPress={(event) => {
                              onSelectSuggestion(suggestion, event)
                            }}
                            tabIndex={0}
                            role='button'>
                            {suggestion.description}
                          </div>
                        ))}
                      </div>
                    )}
                    apiKey={process.env.GATSBY_GOOGLE_API}
                  />
                </Grid>
              )
            })()}

          <Grid item xs={12} md={6} ccss={sPadding(true)}>
            <input
              name='firstName'
              type={content.fields.firstName.type}
              label={content.fields.firstName.label[lang]}
              placeholder={content.fields.firstName.label[lang]}
              required={content.fields.firstName.isRequired.includes(formType)}
              onChange={onValueChange}
              value={formData['firstName']}
              css={sInput}
            />
          </Grid>
          <Grid item xs={12} md={6} ccss={sPadding(false)}>
            <input
              name='lastName'
              type={content.fields.lastName.type}
              label={content.fields.lastName.label[lang]}
              placeholder={content.fields.lastName.label[lang]}
              required={content.fields.lastName.isRequired.includes(formType)}
              onChange={onValueChange}
              value={formData['lastName']}
              css={sInput}
            />
          </Grid>
          {/* {(isCollab || isCarrier || isQuote) && ( */}
          <Grid item xs={12} md={6} ccss={sPadding(true)}>
            {(() => {
              const isRequired =
                content.fields.company.isRequired.includes(formType)
              return (
                <input
                  name='company'
                  type={content.fields.company.type}
                  label={content.fields.company.label[lang]}
                  placeholder={`${content.fields.company.label[lang]} ${
                    isRequired ? ' *' : ''
                  }`}
                  required={isRequired}
                  onChange={onValueChange}
                  value={formData['company']}
                  css={[
                    sInput,
                    wasSent && isRequired && !formData.company && sInputError,
                  ]}
                />
              )
            })()}
          </Grid>
          {/* )} */}
          {/* {(isMessage || isCollab || isCarrier || isQuote) && */}
          {(() => {
            const isRequired = content.fields.nip.isRequired.includes(formType)
            return (
              <Grid item xs={12} md={6} ccss={sPadding(false)}>
                <input
                  name='nip'
                  type={content.fields.nip.type}
                  label={content.fields.nip.label[lang]}
                  placeholder={`${content.fields.nip.label[lang]} ${
                    isRequired ? ' *' : ''
                  }`}
                  required={isRequired}
                  onChange={onValueChange}
                  value={formData['nip']}
                  css={[
                    sInput,
                    wasSent && isRequired && !formData.nip && sInputError,
                  ]}
                />
              </Grid>
            )
          })()}

          {(() => {
            const isRequired =
              content.fields.email.isRequired.includes(formType)
            return (
              <Grid
                item
                xs={12}
                md={6}
                ccss={[{ height: 'max-content' }, sPadding(true)]}>
                <div css={{ position: 'relative', width: '100%' }}>
                  <input
                    name='email'
                    type='text'
                    label={content.fields.email.label[lang]}
                    placeholder={`${content.fields.email.label[lang]} ${
                      isRequired ? ' *' : ''
                    }`}
                    required={isRequired}
                    onChange={onValueChange}
                    value={formData['email']}
                    css={[
                      sInput,
                      wasSent &&
                        isRequired &&
                        !validateEmail(formDataEmail) &&
                        sInputError,
                    ]}
                  />
                  {formDataEmail !== '' && !validateEmail(formDataEmail) && (
                    <div
                      css={{
                        position: 'absolute',
                        top: 16,
                        right: 32,
                        // transform: 'translateY(-50%)',
                        color: 'red',
                      }}>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        height='24'
                        width='24'
                        viewBox='0 0 20 20'
                        fill='currentColor'>
                        <path
                          fillRule='evenodd'
                          d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z'
                          clipRule='evenodd'
                        />
                      </svg>
                    </div>
                  )}
                </div>
              </Grid>
            )
          })()}
          {(() => {
            const isRequired =
              content.fields.phone.isRequired.includes(formType)
            return (
              <Grid item xs={12} md={6} ccss={sPadding(false)}>
                <input
                  name='phone'
                  type={content.fields.phone.type}
                  label={content.fields.phone.label[lang]}
                  placeholder={`${content.fields.phone.label[lang]} ${
                    content.fields.phone.isRequired.includes(formType)
                      ? ' *'
                      : ''
                  }`}
                  required={content.fields.phone.isRequired.includes(formType)}
                  onChange={onValueChange}
                  value={formData['phone']}
                  css={[
                    sInput,
                    wasSent && isRequired && !formData.phone && sInputError,
                  ]}
                />
              </Grid>
            )
          })()}
          {(() => {
            const isRequired =
              content.fields.message.isRequired.includes(formType)
            return (
              <Grid item xs={12} ccss={{ height: '100%', flexGrow: 1 }}>
                <div css={{ width: '100%' }}>
                  <textarea
                    name='message'
                    type='text'
                    placeholder={`${content.fields.message.label[lang]} ${
                      isRequired ? ' *' : ''
                    }`}
                    required={isRequired}
                    onChange={onValueChange}
                    value={formData['message']}
                    rows={3}
                    css={[
                      sInput,
                      sTextArea,
                      wasSent && isRequired && !formData.message && sInputError,
                    ]}
                  />
                </div>
              </Grid>
            )
          })()}
        </Grid>
        <FormAgreements
          formAgreements={formAgreements}
          setFormAgreements={setFormAgreements}
          wasSent={wasSent}
          content={agreementsContent}
          formType={formType}
          onAgreementsChange={(agreementData) => {
            setFormData(prevData => ({
              ...prevData,
              agreements: agreementData
            }));
          }}
        />
        <AdministrativeFormInfo lang={lang} />
        <div
          css={[
            {
              display: 'flex',
              justifyContent: 'center',
              marginTop: '1.5rem',
              alignItems: 'center',
              width: '100%',
            },
            popup && { position: 'sticky', bottom: 0, left: 0 },
          ]}>
          <Button
            noMargin
            center
            reactiveBlack
            active={wasSent ? mailState === 'filled' : true}
            googleId={mailState === 'filled' ? 'send_form' : 'fill_form'}
            dataFormType={formType}
            action={sendMail}>
            {/* {!isRequiredFilled && content.dictonary.fill[lang]}
          {isRequiredFilled &&
            !agreementFilled &&
            content.dictonary.fillagreements[lang]} */}
            {wasSent && mailState !== 'filled'
              ? !agreementFilled
                ? content.dictonary.fillagreements[lang]
                : content.dictonary.fill[lang]
              : content.dictonary.send[lang]}
          </Button>
        </div>
      </div>
      {openBackdrop && (
        <FormFeedback
          popup={popup}
          success={success}
          open={openBackdrop}
          setOpen={setOpenBackdrop}
          dataSuccess={content.dictonary.success}
          dataFailure={content.dictonary.failure}
        />
      )}
    </>
  )
}

const sInputHiderParent = {
  position: 'relative',
  top: 0,
  left: 0,
  '&:hover, &:focus, & > input:focus, & > input:hover': {
    '.input-hider': {
      opacity: 0,
    },
  },
}

const sInputHider = {
  transition: 'opacity 0.2s ease-out',
  position: 'absolute',
  top: 0,
  left: 0,
  height: 56,
  background: `linear-gradient(to right, #E8E8E8 75%, #E8E8E800)`,
  width: 'calc(100% - 3rem)',
  paddingLeft: '2rem',
  borderRadius: '1rem',
  fontSize: '1rem',
  letterSpacing: '-0.05em',
  fontWeight: 700,
  color: colors.text.concat(alpha[60]),
  display: 'flex',
  alignItems: 'center',
  pointerEvents: 'none',
  [s.sm_down]: {
    fontSize: '0.875rem',
  },
  [s.xs]: {
    height: 50,
  },
}

export const sContainer = {
  width: '100%',
  height: 'max-content',
  display: 'flex',
  flexDirection: 'column',
}

export const sFormContainer = {
  marginTop: '1.5rem',
  [s.sm_down]: {
    marginTop: '2rem',
  },
}

const sTypes = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gridColumnGap: '1rem',
  marginBottom: '4rem',

  [s.sm_down]: {
    marginBottom: '2rem',
  },

  [s.xs]: {
    gridColumnGap: '1rem',
  },
}

const sType = {
  cursor: 'pointer',

  fontSize: '0.875rem',
  fontWeight: 700,

  padding: '1rem',
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: colors.text.concat(alpha[8]),
  color: colors.text.concat(alpha[60]),
  borderRadius: '1rem',
  // textTransform: 'uppercase',
  // letterSpacing: '-0.05em',
  transition: 'background-color 0.2s ease-out, color 0.2s ease-out',
  '&:hover': {
    backgroundColor: colors.text.concat(alpha[16]),
    color: colors.text.concat(alpha[100]),
  },
  '&:focus': {
    backgroundColor: colors.text.concat(alpha[30]), // or 16
    color: colors.text.concat(alpha[60]),
  },

  [s.xs]: {
    padding: '1rem',
  },
}

const sTypeActive = {
  backgroundColor: colors.text,
  color: colors.white,
  '&:hover': {
    backgroundColor: colors.text,
    color: colors.white,
  },
  '&:focus': {
    backgroundColor: colors.text,
    color: colors.white,
  },
}

const sIcon = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  svg: {
    width: '1.5rem',
    height: '1.5rem',
  },
  [s.md]: {
    marginRight: '0.5rem',
  },
  [s.lg]: {
    marginRight: '1rem',
  },
}

const sSuggestions = {
  zIndex: 800,
  position: 'absolute',
  left: 0,
  top: 52,
  width: '100%',
  backgroundColor: `white`,
  borderRadius: '1rem',
  overflow: 'hidden',
}

const sSugestion = {
  backgroundColor: colors.greyDark.concat(alpha[12]),
  fontSize: '0.75rem',
  padding: '0.75rem 1.5rem',
  transition: 'color 0.2s ease, background-color 0.2s ease',
  color: colors.text.concat(alpha[60]),
  fontWeight: 700,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: colors.greyDark.concat(alpha[25]),
    color: colors.text,
  },
  letterSpacing: '-0.05em',
  '&:first-of-type': {
    paddingTop: '1rem',
  },
  '&:last-of-type': {
    paddingBottom: '1rem',
  },
}

const sSectionName = {
  fontSize: '1rem',
  fontWeight: 700,
  letterSpacing: '-0.05em',
  color: colors.text,
}

const sCargoLabel = {
  [s.xs]: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    width: '100%',
  },
}

const sButtonExtra = {
  fontSize: '0.75rem',
  padding: '0.75rem 1.5rem',
  letterSpacing: 0,
  width: 'max-content',
  [s.xs]: {
    width: 'max-content',
  },
}

const selectStyles = (wasSent, formData, fieldName) => ({
  container: (base) => ({ ...base, width: '100%', marginBottom: '1.25rem' }),
  control: (base, state) => {
    const isError =
      wasSent && (!formData[fieldName] || formData[fieldName].length === 0)
    return {
      ...base,
      border: '0',
      boxShadow: 'none',
      backgroundColor: isError
        ? colors.red.concat(alpha[8])
        : state.isFocused
        ? colors.greyDark.concat(alpha[16])
        : colors.greyDark.concat(alpha[8]),
      transition: 'color 0.25s linear, background-color 0.25s linear',
      borderRadius: '1rem',
      '&:hover': {
        backgroundColor: colors.greyDark.concat(alpha[4]),
        '.select__placeholder': {
          color: `${colors.text.concat(alpha[50])} !important`,
        },
      },
    }
  },
  valueContainer: (base) => ({
    ...base,
    padding: '1rem 0 1rem 2rem',
    '.select__input-container': {
      margin: '0',
      padding: '0',
      color: colors.text,
    },
    color: colors.text,
  }),
  placeholder: (base) => {
    const isError =
      wasSent && (!formData[fieldName] || formData[fieldName].length === 0)
    return {
      ...base,
      fontWeight: 700,
      color: isError
        ? colors.red.concat(alpha[50])
        : colors.text.concat(alpha[50]),
    }
  },
  indicator: (base) => {
    const isError =
      wasSent && (!formData[fieldName] || formData[fieldName].length === 0)
    return {
      ...base,
      color: isError
        ? colors.red.concat(alpha[50])
        : colors.text.concat(alpha[50]),
    }
  },
  menu: (base) => ({ ...base, borderRadius: '1rem', overflow: 'hidden' }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused
      ? colors.greyDark.concat(alpha[16])
      : state.isSelected
      ? colors.greyDark.concat(alpha[8])
      : 'white',
    '&:hover': {
      backgroundColor: colors.greyDark.concat(alpha[8]),
    },
    color: colors.text,
    fontWeight: 700,
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: colors.greyDark.concat(alpha[30]),
    borderRadius: '1rem',
  }),
  multiValueLabel: (base) => ({
    ...base,
    paddingLeft: '.75rem',
    color: colors.text,
    fontWeight: 700,
  }),
  multiValueRemove: (base) => ({
    ...base,
    paddingRight: '.5rem',
    ':hover': {
      borderRadius: '0 1rem 1rem 0',
    },
  }),
})

export const sPadding = (left) =>
  left
    ? {
        [s.md]: {
          paddingRight: '0.75rem',
        },
      }
    : {
        [s.md]: {
          paddingLeft: '0.75rem',
        },
      }

const types = [
  {
    type: 'QUOTE',
    icon: () => <TruckIcon />,
    name: {
      pl: 'Wycena',
      en: 'Quote',
    },
    title: {
      pl: 'Wycena Transportu',
      en: 'Transport Quote',
    },
    desc: {
      pl: 'Szybka wycena transportu. Napisz do nas i wyceń swój transport.',
      en: 'Szybka wycena transportu. Napisz do nas i wyceń swój transport.',
    },
  },
  {
    type: 'MESSAGE',
    icon: () => <MessageIcon />,
    name: {
      pl: 'Zapytanie',
      en: 'Message',
    },
    title: {
      pl: 'Szybkie Zapytanie',
      en: 'Quick Message',
    },
    desc: {
      pl: 'Masz do nas pytanie? Napisz do nas. ',
      en: 'Masz do nas pytanie? Napisz do nas. ',
    },
  },
  {
    type: 'COLLAB',
    icon: () => <DocumentIcon />,
    name: {
      pl: 'Przetargi',
      en: 'Collab',
    },
    title: {
      pl: 'Przetargi',
      en: 'Collaboration',
    },
    desc: {
      pl: 'Zapraszamy do wypełnienia oferty przetargowej i nawiązania współpracy z Omida VLS.',
      en: 'Zapraszamy do współpracy Klientów. Napisz do nas, aby otrzymać ofertę współpracy.',
    },
  },
  {
    type: 'CARRIER',
    icon: () => <ArchiveIcon />,
    name: {
      pl: 'Przewoźnik',
      en: 'Carrier',
    },
    title: {
      pl: 'Formularz Przewoźnika',
      en: 'Carrier Form',
    },
    desc: {
      pl: 'Zapraszamy do współpracy przewoźników.',
      en: 'Zapraszamy do współpracy przewoźników.',
    },
  },
]
const content = {
  sections: {
    cargos: {
      pl: 'Ładunki',
      en: 'Cargo',
    },
    contact: {
      pl: 'Informacje Kontaktowe',
      en: 'Contact Information',
    },
  },
  fields: {
    placeFrom: {
      label: { pl: 'Miejsce Załadunku', en: 'Loading Place' },
      placeholder: { pl: ' ', en: ' ' },
      type: 'autocomplete',
      isRequired: ['QUOTE'],
    },
    placeTo: {
      label: { pl: 'Miejsce Rozładunku', en: 'Unloading Place' },
      placeholder: { pl: ' ', en: ' ' },
      type: 'autocomplete',
      isRequired: ['QUOTE'],
    },
    dateStart: {
      label: { pl: 'Data Załadunku', en: 'Loading Date' },
      placeholder: { pl: '', en: '' },
      type: 'date',
      isRequired: [],
    },
    dateEnd: {
      label: { pl: 'Data Rozładunku', en: 'Unloading Date' },
      placeholder: { pl: '', en: '' },
      type: 'date',
      isRequired: [],
    },
    tenderDateStart: {
      label: { pl: 'Data ogłoszenia przetargu *', en: 'Loading Date' },
      placeholder: { pl: '', en: '' },
      type: 'date',
      isRequired: ['COLLAB'],
    },
    tenderDateEnd: {
      label: { pl: 'Data zamknięcia przetargu *', en: 'Unloading Date' },
      placeholder: { pl: '', en: '' },
      type: 'date',
      isRequired: ['COLLAB'],
    },
    message: {
      label: { pl: 'Wiadomość', en: 'Message' },
      placeholder: { pl: '', en: '' },
      type: 'message',
      isRequired: ['MESSAGE'],
    },
    email: {
      label: { pl: 'Email', en: 'Email' },
      placeholder: { pl: '', en: '' },
      type: 'email',
      isRequired: ['MESSAGE', 'QUOTE', 'COLLAB', 'CARRIER'],
    },
    phone: {
      label: { pl: 'Telefon', en: 'Number' },
      placeholder: { pl: '', en: '' },
      type: 'tel',
      isRequired: ['MESSAGE', 'QUOTE', 'COLLAB', 'CARRIER'],
    },
    firstName: {
      label: { pl: 'Imię', en: 'Firstname' },
      placeholder: { pl: '', en: '' },
      type: 'text',
      isRequired: [],
    },
    lastName: {
      label: { pl: 'Nazwisko', en: 'Lastname' },
      placeholder: { pl: '', en: '' },
      type: 'text',
      isRequired: [],
    },
    company: {
      label: { pl: 'Firma', en: 'Company' },
      placeholder: { pl: '', en: '' },
      type: 'text',
      isRequired: ['CARRIER'],
    },
    nip: {
      label: { pl: 'NIP', en: 'TAX ID' },
      placeholder: { pl: '', en: '' },
      type: 'string',
      isRequired: ['MESSAGE', 'QUOTE', 'COLLAB', 'CARRIER'],
    },
    width: {
      label: { pl: 'Szerokość, cm', en: 'Width, cm' },
      placeholder: { pl: 'centymetry', en: 'centimeters' },
      type: 'number',
      isRequired: [],
    },
    length: {
      label: { pl: 'Długość, cm', en: 'Length, cm' },
      placeholder: { pl: 'centymetry', en: 'centimeters' },
      type: 'number',
      isRequired: [],
    },
    height: {
      label: { pl: 'Wysokość, cm', en: 'Height, cm' },
      placeholder: { pl: 'centymetry', en: 'centimeters' },
      type: 'number',
      isRequired: [],
    },
    weight: {
      label: { pl: 'Ciężar, kg', en: 'Weight, kg' },
      placeholder: { pl: 'kilogramy', en: 'kilograms' },
      type: 'number',
      isRequired: [],
    },
    quantity: {
      label: { pl: 'Ilość', en: 'Quantity' },
      placeholder: { pl: '', en: '' },
      type: 'number',
      isRequired: [],
    },
    carrierServiceType: {
      label: { pl: 'Gdzie chcesz jeździć? *', en: 'Company' },
      placeholder: { pl: '', en: '' },
      type: 'select',
      firstOption: {
        value: { pl: '', en: '' },
        label: { pl: 'Gdzie chcesz jeździć? *', en: '' },
      },
      options: [
        { value: 'domestic', label: { pl: 'Transport Krajowy', en: '' } },
        {
          value: 'international',
          label: { pl: 'Transport Międzynarodowy', en: '' },
        },
      ],
      isRequired: ['CARRIER'],
    },
    carrierTrailerType: {
      label: { pl: 'Jaki rodzaj naczep posiadasz? *', en: 'Company' },
      placeholder: { pl: '', en: '' },
      type: 'select',
      firstOption: {
        value: { pl: '', en: '' },
        label: { pl: 'Jaki rodzaj naczepy posiadasz? *', en: '' },
      },
      options: [
        { value: 'plandeka', label: { pl: 'Plandeka', en: '' } },
        { value: 'mega', label: { pl: 'Mega', en: '' } },
        { value: 'chlodnia', label: { pl: 'Chłodnia', en: '' } },
        { value: 'izoterma', label: { pl: 'Izoterma', en: '' } },
        { value: 'wywrotka', label: { pl: 'Wywrotka', en: '' } },
        { value: 'kontenerowiec', label: { pl: 'Kontenerowiec', en: '' } },
        {
          value: 'inne',
          label: { pl: 'Interesuje mnie użytkownie naczepy Omida', en: '' },
        },
      ],
      isRequired: ['CARRIER'],
    },
    carrierCarType: {
      label: { pl: 'Jaki rodzaj pojazdu/ów posiadasz? *', en: 'Company' },
      placeholder: { pl: '', en: '' },
      type: 'select',
      firstOption: {
        value: { pl: '', en: '' },
        label: { pl: 'Jaki rodzaj pojazdu posiadasz? *', en: '' },
      },
      options: [
        { value: 'bus', label: { pl: 'Bus', en: '' } },
        { value: 'solowka', label: { pl: 'Solówka', en: '' } },
        { value: 'zestaw', label: { pl: 'Zestaw 13,6', en: '' } },
        {
          value: 'ciagnik',
          label: { pl: 'Ciągnik siodłowy (bez naczepy)', en: '' },
        },
      ],
      isRequired: ['CARRIER'],
    },
    carrierTruckType: {
      label: { pl: 'Rodzaj ciągnika/ów, które posiadasz *', en: 'Company' },
      placeholder: { pl: '', en: '' },
      type: 'select',
      firstOption: {
        value: { pl: '', en: '' },
        label: { pl: 'Rodzaj ciągnika/ów, które posiadasz *', en: '' },
      },
      options: [
        {
          value: 'standard',
          label: {
            pl: 'Ciągnik siodłowy z możliwością podpięcia tylko naczep STANDARD',
            en: '',
          },
        },
        {
          value: 'mega',
          label: {
            pl: 'Ciągnik siodłowy z możliwością podpięcia tylko naczep MEGA',
            en: '',
          },
        },
        {
          value: 'both',
          label: {
            pl: 'Ciągnik siodłowy uniwersalny z możliwością podpięcia naczep STANDARD i MEGA',
            en: '',
          },
        },
      ],
      isRequired: [],
    },
    carrierDrivingSystem: {
      label: { pl: 'Jaki preferujesz system prowadzenia aut? *', en: 'Company' },
      placeholder: { pl: '', en: '' },
      type: 'select',
      firstOption: { value: {pl: '', en: ''}, label: {pl: 'Jaki preferujesz system prowadzenia aut? *', en: ''} },
      options: [
        { value: 'weekly', label: {pl: 'Tygodniowy', en: ''} },
        { value: 'fortnightly', label: {pl: 'Dwutygodniowy', en: ''} },
        { value: 'elastic', label: {pl: 'Elastyczny', en: ''} },
      ],
      isRequired: ['CARRIER'],
    },
    carrierLocation: {
      label: { pl: 'Miejsce bazy', en: 'Loading Place' },
      placeholder: { pl: ' ', en: ' ' },
      type: 'autocomplete',
      isRequired: ['CARRIER'],
    },
  },
  dictonary: {
    cargoAdd: {
      pl: 'Dodaj ładunek',
      en: 'Add cargo',
    },
    cargoDelete: {
      pl: 'Usuń ładunek',
      en: 'Delete cargo',
    },
    success: {
      title: { pl: 'Dziękujemy!', en: 'Thank you!' },
      text: {
        pl: 'Twoja wiadomość została wysłana. Odpowiemy najszybciej jak to możliwe.',
        en: 'Your message has been sent. We will reply as soon as possible.',
      },
    },
    failure: {
      title: { pl: 'Niepowodzenie!', en: 'Failure!' },
      text: {
        pl: 'Wygląda na to, że Twoja wiadomość nie została wysłana. Skontaktuj się z nami drogą mailową lub telefonicznie',
        en: 'It looks like your message has not been sent. Contact us by e-mail or call us',
      },
    },
    send: {
      pl: 'Wyślij',
      en: 'Send',
    },
    fill: {
      pl: 'Wypełnij formularz',
      en: 'Fill up the form',
    },
    fillagreements: {
      pl: 'Zaznacz zgodę',
      en: 'Agree to the terms',
    },
    showMore : {
      pl: 'Pokaż więcej',
      en: 'Show more'
    },
    showLess : {
      pl: 'Pokaż mniej', 
      en: 'Show less'
    }
  },
}

export default FormDynamic